import { PropsWithChildren, useState } from 'react';

import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { NotifyMeForm } from '~/components/notify-me-form';
import { Program } from '~/features/program/models/program';
import { ProgramContent } from '~/features/program/models/program-content';
import { buildSyllabusUrlWithAutoDownload } from '~/features/syllabus/utils/syllabus-download-query';
import { useUser } from '~/features/user/hooks/use-user';
import { User } from '~/features/user/models/user';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import { CloseIcon, DownloadIcon } from '../icons';

export type DownloadSyllabusButtonProps = StaticDownloadSyllabusButtonProps & {
  onClick?: () => void;
  onFormSubmit?: (formData: any) => void;
  onAuthClick?: (user?: User) => void;
  /**
   * Skips the full-bleed ContentContainer wrapper so the button can sit inside a
   * section that already applies its own horizontal padding. Nesting a second
   * ContentContainer would double the gutter and misalign the button.
   */
  isInline?: boolean;
};

export function DownloadSyllabusButton({
  programKey,
  form,
  isInline = false,
  onClick,
  onFormSubmit,
  onAuthClick,
}: DownloadSyllabusButtonProps) {
  const { t } = useI18n();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [status, setStatus] = useState<'idle' | 'submitted'>('idle');
  const { isUserAuthenticated, userIsLoading, user } = useUser();
  const clientRender = useClientRender();
  const toast = useToast();
  const linkDestination = buildSyllabusUrlWithAutoDownload(programKey);

  function clickHandler() {
    onClick?.();

    if (status === 'submitted' || isUserAuthenticated) {
      if (isUserAuthenticated) {
        onAuthClick?.(user);
      }
      window.open(linkDestination, '_blank');
    } else {
      onOpen();
    }
  }

  if (!form) {
    return null;
  }

  return (
    <ButtonContainer isInline={isInline}>
      <Button
        isLoading={clientRender && userIsLoading}
        leftIcon={<DownloadIcon height='20px' width='20px' />}
        loadingText={t('common.loadingEllipsis')}
        marginStart='auto'
        size='icon-left'
        variant='outline'
        width={{
          base: '100%',
          md: 'fit-content',
        }}
        onClick={clickHandler}
      >
        {t('program.downloadSyllabus')}
      </Button>
      <Modal isOpen={isOpen} size='xl' onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <Heading as={ModalHeader} size='heading-5'>
            {t('program.requestSyllabus')}
          </Heading>
          <ModalCloseButton as={CloseIcon} color='supportGray.30' cursor='pointer' height='24px' width='24px' />
          <ModalBody>
            <NotifyMeForm
              templateForm={form}
              renderFormFields={(formFields) => (
                <>
                  <Box
                    display='flex'
                    flex='1'
                    gap={4}
                    flexDir={{
                      base: 'column',
                      lg: 'row',
                    }}
                  >
                    {formFields[0]}
                    {formFields[1]}
                  </Box>
                  {formFields.slice(2)}
                </>
              )}
              isFullWidth
              onCancelClick={onClose}
              onError={() =>
                toast({
                  variant: 'error',
                  title: 'Oops. Something went wrong. Please try again.',
                })
              }
              onSubmit={(formData) => {
                onFormSubmit?.(formData);
                setStatus('submitted');
                window.open(linkDestination, '_blank');
                toast({
                  title: 'Thank you for your interest!',
                  variant: 'success',
                });
                onClose();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </ButtonContainer>
  );
}

function ButtonContainer({ isInline, children }: PropsWithChildren<{ isInline: boolean }>) {
  if (isInline) return <>{children}</>;

  return (
    <Box>
      <ContentContainer alignItems='center' display='flex'>
        {children}
      </ContentContainer>
    </Box>
  );
}

export function transformStaticDownloadSyllabusButtonProps({
  programContent,
  program,
}: {
  programContent: ProgramContent;
  program: Program;
}) {
  return {
    programKey: program.key,
    form: programContent.downloadSyllabusForm,
  };
}

export type StaticDownloadSyllabusButtonProps = ReturnType<typeof transformStaticDownloadSyllabusButtonProps>;
